import {
  ITransform,
  parseArray,
  parseNumber,
  parseString,
  transformModel,
} from '~/src/dash/api/resource-transforms';
import { addDomainAndTokenToUrl, IHttpResponse, restGet, restPost } from '~/src/dash/api/rest';

export interface ICreativeValidation {
  ID: number;
  CreatedAt?: string;
  UpdatedAt?: string;
  DeletedAt?: string;

  OriginalCreativeURL: string;
  CreativeURL: string;
  Adomain: string;
  AdomainEnrichedBy: string;
  IABCategory: string;
  CategoryEnrichedBy: string;
  PublisherID: number;
  Impressions: number;
  BidErrors: number;
  BidErrorBlockedResponses: number;
  BidErrorsBrandSafety: number;
  BidderID: number;
  BidderName: string;
  TotalCount: number;
  ReviewStatus: number;
  Notes: string;
  AdomainOverride: string;
  IABCategoryOverride: string;
  BrandSafetyRuleIDs: number[];
  User: string;
}

export const REVIEW_STATUS_BLOCKED_BY_API: number = 4;
export const REVIEW_STATUS_ALLOWED: number = 3;
export const REVIEW_STATUS_BLOCKED: number = 2;
export const REVIEW_STATUS_REVIEWED: number = 1;
export const REVIEW_STATUS_UNREVIEWED: number = 0;

// |------------------ CreativeValidation Transformations ------------------|
const transformCreativeValidationToApiConfig: ITransform = {
  OriginalCreativeURL: parseString(['OriginalCreativeURL']),
  CreativeURL: parseString(['CreativeURL']),
  Adomain: parseString(['Adomain']),
  IABCategory: parseString(['IABCategory']),
  PublisherID: parseNumber(['PublisherID']),
  ReviewStatus: parseNumber(['ReviewStatus']),
  BidderID: parseNumber(['BidderID']),
  Notes: parseString(['Notes']),
  AdomainOverride: parseString(['AdomainOverride']),
  AdomainEnrichedBy: parseString(['AdomainEnrichedBy']),
  IABCategoryOverride: parseString(['IABCategoryOverride']),
  IABCategoryEnrichedBy: parseString(['IABCategoryEnrichedBy']),
  BrandSafetyRuleIDs: parseArray(['BrandSafetyRuleIDs'], (obj: any) => {
    return obj;
  }),
};

// |------------------ Creative Validation API CALLS ------------------|
export const transformCreativeValidationToApi = transformModel<ICreativeValidation>(
  transformCreativeValidationToApiConfig,
);

export async function getCreativeValidationsV2(
  publisherID: string,
  review_status: number,
  creativeValidation: { [key: string]: any },
): Promise<ICreativeValidation[]> {
  const reqUrl = addDomainAndTokenToUrl(
    `/publishers/${publisherID}/creative_validation/` + review_status,
  );
  return await restPost<ICreativeValidation[]>(reqUrl, creativeValidation).then(
    (val: IHttpResponse<ICreativeValidation[]>) => {
      return val.parsedBody || [];
    },
  );
}

// Post Update ReviewStatus for given creative_url and publisher_id
// /publishers/{publisherID}/update_creative_validations
export async function updateCreativeValidation(
  publisherID: string,
  creativeValidation: Partial<ICreativeValidation>,
): Promise<ICreativeValidation | undefined> {
  const reqUrl = addDomainAndTokenToUrl(`/publishers/${publisherID}/update_creative_validation`);
  const parsedCreativeValidation = transformCreativeValidationToApi(creativeValidation);
  return await restPost<ICreativeValidation>(reqUrl, parsedCreativeValidation).then(
    (val: IHttpResponse<ICreativeValidation>) => {
      return val.parsedBody;
    },
  );
}

// Post Batch Update ReviewStatus for given creative_url and publisher_id
// /publishers/{publisherID}/batch_update_creative_validation_status
export async function batchUpdateCreativeValidationStatus(
  publisherID: string,
  creativeValidations: Partial<ICreativeValidation>[],
  newReviewStatus: number,
): Promise<[unknown, string | undefined]> {
  const reqUrl = addDomainAndTokenToUrl(
    `/publishers/${publisherID}/batch_update_creative_validation_status`,
  );
  const body = { creativeValidations, newReviewStatus };

  try {
    return [(await restPost(reqUrl, body, undefined)).parsedBody, undefined];
  } catch (e) {
    return [undefined, e];
  }
}

export async function getCreativeValidationUsersByPublisher(
  publisherID: string,
  reviewStatus: number,
): Promise<string[]> {
  const reqUrl = addDomainAndTokenToUrl(
    `/publishers/${publisherID}/creative_validation_users_by_publisher/` + reviewStatus,
  );
  return await restGet<string[]>(reqUrl).then((val: IHttpResponse<string[]>) => {
    return val.parsedBody || [];
  });
}
